import { isPlainObject } from 'lodash';

const UPLOAD_TYPE = {
  PLAYBOOK: 'playbook',
  CONNECTION: 'connection',
};

export default UPLOAD_TYPE;

// Loosely resolve the upload type from any file name that *contains* "playbook"
// or "connection" (e.g. "playbook (1).yaml", "newplaybook.yaml", "connectionv2.yml").
// Returns null when the name has both keywords or neither — the caller then lets
// the user choose the type explicitly.
export function detectUploadType(rawName) {
  const lower = String(rawName ?? '').toLowerCase();
  const hasPlaybook = lower.includes(UPLOAD_TYPE.PLAYBOOK);
  const hasConnection = lower.includes(UPLOAD_TYPE.CONNECTION);

  if (hasPlaybook === hasConnection) {
    return null;
  }

  return hasPlaybook ? UPLOAD_TYPE.PLAYBOOK : UPLOAD_TYPE.CONNECTION;
}

// Resolve the upload type from the parsed file *contents* — the authoritative signal.
// A connection is either an HTTP endpoint schema (its `default` is a request template
// with an endpoint/method) or an SDK connection template (a JSON schema with
// properties + required). A playbook has transform stanzas (global/eventSpecific/modes,
// or a `default` of mapping/enrichment/parameter ops). Returns null when neither is
// clearly indicated, so the caller can fall back to the file name.
export function detectUploadTypeFromContent(result) {
  if (!isPlainObject(result)) {
    return null;
  }
  if (isConnectionContent(result)) {
    return UPLOAD_TYPE.CONNECTION;
  }
  if (isPlaybookContent(result)) {
    return UPLOAD_TYPE.PLAYBOOK;
  }
  return null;
}

function isConnectionContent(result) {
  if (result.properties && result.required) {
    return true;
  }
  return isPlainObject(result.default) && Boolean(result.default.endpoint || result.default.method);
}

function isPlaybookContent(result) {
  if (result.global || result.eventSpecific || result.modes) {
    return true;
  }
  const { default: defaultStanza } = result;
  return (
    isPlainObject(defaultStanza) &&
    Boolean(defaultStanza.mappings || defaultStanza.enrichments || defaultStanza.parameters)
  );
}

export function isPlaybookUpload(uploadType) {
  return uploadType === UPLOAD_TYPE.PLAYBOOK;
}

export function isConnectionUpload(uploadType) {
  return uploadType === UPLOAD_TYPE.CONNECTION;
}
